import { CORRECTOPTIONTOOPTIONFIELDMAP, OPTIONFIELDTOCORRECTOPTIONMAP } from "./Constants";

// Format a plan price as "USD first, then BDT" — e.g. "$5 · ৳600". Charging is in USD, so USD
// leads; BDT follows for local context. Falls back to BDT-only when no USD figure is available.
export function formatUsdBdt(usd, bdt) {
    const hasUsd = usd != null && Number(usd) > 0;
    const bdtStr = `৳${bdt}`;
    return hasUsd ? `$${usd} · ${bdtStr}` : bdtStr;
}

// Serialize a landing/anonymous answer into the `selected_option` string the public grade
// endpoint expects, for any question type. `ans` may be:
//   - a letter string "A"            → single-select MCQ
//   - an array of letters ["A","C"]  → multi-select MCQ / Sentence Equivalence
//   - an object { blanks: {0:1,...} }→ Text Completion (ordered per-blank choice indices)
export function serializeLandingAnswer(ans) {
    if (ans == null) return "";
    if (Array.isArray(ans)) return [...ans].sort().join(",");
    if (typeof ans === "object" && ans.blanks) {
        return Object.keys(ans.blanks).map(Number).sort((a, b) => a - b).map(i => ans.blanks[i]).join(",");
    }
    return String(ans);
}


// refresh_token, user_state). SameSite=Strict is safe here because these cookies
// are never auto-sent to the API — auth travels in the x-auth-jwt header — so
// Strict has no downside and blocks cross-site sends. Secure is enabled only on
// HTTPS pages so local http dev (localhost / LAN IP) still works.
export function cookieOptions() {
    const isHttps = typeof window !== 'undefined' && window.location?.protocol === 'https:';
    return { sameSite: 'Strict', secure: isHttps };
}

// First `n` whitespace-separated words of a string, with an ellipsis when truncated. Used for
// compact question/passage previews in grids (full text shown via a hover title).
export function firstWords(text, n = 10) {
    const s = (text == null ? '' : String(text)).trim();
    if (!s) return '';
    const words = s.split(/\s+/);
    return words.length <= n ? s : words.slice(0, n).join(' ') + '…';
}

// Collapse all line breaks (and runs of whitespace) into one clean paragraph, dropping blank
// lines. Only whitespace is touched, so LaTeX ($...$) and other content survive intact. Used by
// the "Beautify" button on explanation fields.
export function beautifyText(text) {
    return String(text == null ? '' : text)
        .replace(/\r\n?/g, '\n')
        .split('\n')
        .map(l => l.trim())
        .filter(l => l !== '')
        .join(' ')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();
}

export function isEmpty(input) {
    if (input === null || input === undefined) {
        return true;
    }

    // Handle string and array
    if (typeof input === 'string' || Array.isArray(input)) {
        return input.length === 0;
    }

    // Handle Map, Set
    if (input instanceof Map || input instanceof Set) {
        return input.size === 0;
    }

    if (input instanceof Date) {
        return isNaN(input.getTime()); // Invalid Date => empty
    }

    // Handle plain objects
    if (typeof input === 'object') {
        return Object.keys(input).length === 0;
    }

    // Fallback: treat all other types (numbers, booleans, functions, etc.) as non-empty
    return false;
}

export function getTotalQuestions(data) {
    let total = 0;
    for (const subject in data) {
      if (Array.isArray(data[subject])) {
        total += data[subject].length;
      }
    }
    return total;
}

export function getAnswerFromCorrectOption(question, correctionOption) {
    if (correctionOption === "A" || correctionOption === "B" || correctionOption === "C" || correctionOption === "D") {
        return question[CORRECTOPTIONTOOPTIONFIELDMAP[correctionOption]];
    }
    return "N/A";
}

export function constructFavoriteQuestionIdToBoolMap(input){
    const questionMap = {};

    for (let i = 0; i < input.length; i++) {
    const questionId = input[i].question_id;
    questionMap[questionId] = true;
    }
    return questionMap;
}

// Build the submission answers array from the in-progress answer map.
// A per-question value may be:
//   - a string option key ("option_a")            → single-answer MCQ
//   - an array of option keys (["option_a", ...])  → multi-answer MCQ / Sentence Equivalence
//   - an object { text: "..." }                    → free-text (TEXT) answer
//   - an object { blanks: { 0: 1, 1: 0 } }         → Text Completion (ordered per-blank choice
//                                                     indices, serialized as a CSV like "1,0")
export function buildSelectedOptionsJson(input) {
    const result = [];
    for (const key in input) {
      if (!input.hasOwnProperty(key)) continue;
      const value = input[key];
      const entry = { question_id: Number(key) };
      if (value && typeof value === "object" && !Array.isArray(value) && "blanks" in value) {
        const map = value.blanks || {};
        const indices = Object.keys(map).map(Number).sort((a, b) => a - b).map((i) => map[i]);
        entry.selected_option = indices.join(",");
      } else if (value && typeof value === "object" && !Array.isArray(value) && "order" in value) {
        // Build a Sentence: ordered tile indices, submitted verbatim as "2,0,1".
        entry.selected_option = Array.isArray(value.order) ? value.order.join(",") : "";
      } else if (value && typeof value === "object" && !Array.isArray(value) && "text" in value) {
        entry.selected_option = "";
        entry.text_answer = value.text || "";
      } else if (Array.isArray(value)) {
        entry.selected_option = value
          .map((k) => OPTIONFIELDTOCORRECTOPTIONMAP[k])
          .filter(Boolean)
          .sort()
          .join(",");
      } else {
        // MCQ single stores an option key ("option_a" → "A"); MATCHING stores a raw box token
        // ("G", or word-bank letters beyond H / roman numerals) which is submitted verbatim.
        entry.selected_option = OPTIONFIELDTOCORRECTOPTIONMAP[value] || value || "";
      }
      result.push(entry);
    }
    return result;
}

// In-progress quiz answers are persisted to localStorage keyed by submission_id
// so a mid-quiz reload (then "Resume Quiz") restores the user's selections.
// Mirrors the localStorage idiom in contexts/levelHook.js, JSON-encoded since
// userAnswers is an object { questionId: optionKey }. The entry is cleared on
// a successful submit.
export function inProgressAnswersKey(submissionId) {
    return `quiz_answers_${submissionId}`;
}

export function loadInProgressAnswers(submissionId) {
    if (!submissionId) return {};
    try {
        const raw = localStorage.getItem(inProgressAnswersKey(submissionId));
        return raw ? JSON.parse(raw) : {};
    } catch (error) {
        console.error("Error reading in-progress answers from localStorage:", error);
        return {};
    }
}

export function saveInProgressAnswers(submissionId, answers) {
    if (!submissionId) return;
    try {
        localStorage.setItem(inProgressAnswersKey(submissionId), JSON.stringify(answers));
    } catch (error) {
        console.error("Error writing in-progress answers to localStorage:", error);
    }
}

export function clearInProgressAnswers(submissionId) {
    if (!submissionId) return;
    try {
        localStorage.removeItem(inProgressAnswersKey(submissionId));
    } catch (error) {
        console.error("Error clearing in-progress answers from localStorage:", error);
    }
}

// TOEFL model-test position (which module the taker is in + whether it's active), so a
// mid-test reload drops the user straight back into the module they left. Answers are kept
// separately via the in-progress-answers helpers above.
export function toeflProgressKey(submissionId) {
    return `toefl_pos_${submissionId}`;
}
export function loadToeflProgress(submissionId) {
    if (!submissionId) return null;
    try {
        const raw = localStorage.getItem(toeflProgressKey(submissionId));
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        console.error("Error reading TOEFL progress from localStorage:", error);
        return null;
    }
}
export function saveToeflProgress(submissionId, pos) {
    if (!submissionId) return;
    try {
        localStorage.setItem(toeflProgressKey(submissionId), JSON.stringify(pos));
    } catch (error) {
        console.error("Error writing TOEFL progress to localStorage:", error);
    }
}
export function clearToeflProgress(submissionId) {
    if (!submissionId) return;
    try {
        localStorage.removeItem(toeflProgressKey(submissionId));
    } catch (error) {
        console.error("Error clearing TOEFL progress from localStorage:", error);
    }
}

export function modifyQuizzesSubmissionDetails(fetchedQuizSubmissionDetails) {
    const result = {};

    for (let i = 0; i < fetchedQuizSubmissionDetails.length; i++) {
        const quizSubmission = fetchedQuizSubmissionDetails[i];
        const quizId = quizSubmission.quiz_id;
        result[quizId] = quizSubmission;
    }

    return result;
}

export function extractQuestionIdsBySubject(inputJson) {
    const result = {};
    for (const subject in inputJson) {
        const questions = inputJson[subject];
        result[subject] = questions.map(q => q.question_id);
    }
    return result;
}

export function extractQuestionIds(inputJson) {
    const result = [];

    for (const subject in inputJson) {
      if (Object.prototype.hasOwnProperty.call(inputJson, subject)) {
        const questions = inputJson[subject];
        const questionIds = questions.map(q => q.question_id);
        result.push(...questionIds); // Use spread operator to flatten
      }
    }

    return result;
  }

// Bangla Unicode block U+0980–U+09FF. Mirrors the heuristic in
// jobprep-api/apps/ingestion/extractors/pdf.py: if a meaningful share of the
// non-whitespace characters fall in that block, treat the text as Bangla.
export function looksBangla(text) {
    if (typeof text !== 'string' || text.length === 0) return false;
    let bangla = 0;
    let nonWs = 0;
    for (const ch of text) {
        if (/\s/.test(ch)) continue;
        nonWs += 1;
        const code = ch.codePointAt(0);
        if (code >= 0x0980 && code <= 0x09FF) bangla += 1;
    }
    if (nonWs === 0) return false;
    return bangla / nonWs >= 0.2;
}

const TERMINAL_RE = /[.!?…।]$/;
// First word of an English sentence that signals a question -> end with '?'.
const INTERROGATIVE_EN = new Set([
    'what', 'why', 'how', 'when', 'where', 'who', 'whom', 'whose', 'which',
    'is', 'are', 'am', 'was', 'were', 'do', 'does', 'did', 'can', 'could',
    'should', 'would', 'will', 'shall', 'may', 'might', 'has', 'have', 'had',
]);

// Bangla interrogatives -> question. Curated; extend as needed.
const BN_QUESTION_WORDS = new Set([
    'কি', 'কী', 'কেন', 'কোথায়', 'কোথা', 'কখন', 'কবে', 'কে', 'কারা', 'কাকে', 'কার', 'কাদের',
    'কোন', 'কোনটি', 'কত', 'কতটা', 'কতটুকু', 'কয়টি', 'কেমন', 'কীভাবে', 'কিভাবে', 'কীসের', 'কিসের',
]);
// Common sentence-final verb forms -> statement. Matched against the last word.
const BN_STATEMENT_ENDINGS = [
    'হয়', 'হয়েছে', 'হবে', 'হল', 'হলো', 'ছিল', 'ছিলেন', 'আছে', 'নেই', 'রয়েছে',
    'করে', 'করেন', 'করেছে', 'করেছিল', 'করবে', 'যায়', 'যাবে', 'গেছে', 'গিয়েছে',
    'দেয়', 'দিয়েছে', 'পায়', 'পারে', 'থাকে', 'বলে', 'জানায়', 'এসেছে', 'চায়', 'খায়',
];

// Decide a Bangla sentence's terminal mark from its words.
// Returns '?' (question), '।' (statement), or null (type not detected).
function banglaTerminal(words) {
    const clean = words
        .map(w => w.replace(/^[।?!.,;:\-—'"()]+|[।?!.,;:\-—'"()]+$/g, ''))
        .filter(Boolean);
    const last = clean[clean.length - 1] || '';
    if (clean.some(w => BN_QUESTION_WORDS.has(w)) || last === 'কি' || last === 'কিনা') return '?';
    if (BN_STATEMENT_ENDINGS.some(suf => last === suf || last.endsWith(suf))) return '।';
    return null;
}

// Tidy a field's text on blur: sentence-case the first letter of each sentence
// and add a terminal mark when it reads like a full sentence (>= 3 words).
// Bangla-aware: no casing (Bangla has none); question -> '?', statement -> '।'.
// When `dashWhenUnsure` (the Question Text field), an undetectable Bangla type
// or an incomplete (< 3 word) sentence ends with ' -' as a manual-fix nudge;
// other fields fall back to proper punctuation and leave fragments unchanged.
export function normalizeFieldText(raw, { dashWhenUnsure = false } = {}) {
    if (typeof raw !== 'string') return raw;
    const text0 = raw.trim();
    if (!text0) return text0;
    // Capitalize the first letter of each sentence. toUpperCase() is a no-op for
    // Bangla, so matching only [a-z] safely leaves Bangla untouched / handles mixed text.
    let text = text0.replace(/(^|[.!?…।]\s+)([a-z])/g, (_, pre, ch) => pre + ch.toUpperCase());
    // Already ended (terminal mark, or our ' -' fallback) -> leave as-is.
    if (TERMINAL_RE.test(text) || / -$/.test(text)) return text;

    const words = text.split(/\s+/).filter(Boolean);
    if (words.length >= 3) {
        if (looksBangla(text)) {
            const mark = banglaTerminal(words);
            text += mark || (dashWhenUnsure ? ' -' : '।');
        } else {
            const first = words[0].toLowerCase().replace(/[^a-z]/g, '');
            text += INTERROGATIVE_EN.has(first) ? '?' : '.';
        }
    } else if (dashWhenUnsure) {
        text += ' -'; // incomplete Question Text -> nudge for manual fix
    }
    return text;
}

